import { CurrencyInputV2, InputV2 } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { formatFieldValueToDisplay, formatFieldValueToUpdate } from '../../features/worksheet-v2/custom-worksheet-v2/utils';
import { FieldType, TextAlignment } from '../../types/custom-worksheet-v2';
import { NatureEnum } from '../../types/nature';
import FieldIcon from './FieldIcon';

type CWFieldInputProps = {
    autoFocus?: boolean;
    showIcon?: boolean;
    fieldType: FieldType;
    textAlign?: TextAlignment;
    defaultValue: string | number | null;
    onValueChange: (v: string | number | null) => void;
};

function CWFieldInput({ autoFocus, showIcon = true, fieldType, defaultValue, onValueChange, textAlign }: CWFieldInputProps): JSX.Element {
    switch (fieldType) {
        case FieldType.ACCOUNTING:
            return (
                <FieldInput
                    fieldType={fieldType}
                    showLeftIcon={false}
                    showDropdown={true}
                    defaultValue={defaultValue}
                    autoFocus={autoFocus}
                    onValueChange={onValueChange}
                    dropdownOptions={[
                        { label: 'Dr', value: 'DEBIT' },
                        { label: 'Cr', value: 'CREDIT' },
                    ]}
                />
            );
        case FieldType.MONEY:
        case FieldType.TEXT:
            return (
                <FieldInput
                    fieldType={fieldType}
                    characterLimit={150}
                    defaultValue={defaultValue}
                    autoFocus={autoFocus}
                    onValueChange={onValueChange}
                    showLeftIcon={showIcon}
                    textAlign={textAlign}
                />
            );
        case FieldType.DATE:
            return (
                <FieldInput
                    fieldType={fieldType}
                    type="date"
                    defaultValue={defaultValue}
                    autoFocus={autoFocus}
                    onValueChange={onValueChange}
                    showLeftIcon={showIcon}
                />
            );
        case FieldType.NUMBER:
            // Regex to validate any positive or negative number
            return (
                <FieldInput
                    fieldType={fieldType}
                    regexExp={'^[-]?[0-9]*.?[0-9]*'}
                    defaultValue={defaultValue}
                    autoFocus={autoFocus}
                    onValueChange={onValueChange}
                    showLeftIcon={showIcon}
                />
            );
        case FieldType.PERCENT:
            return (
                <FieldInput
                    fieldType={fieldType}
                    textAlign="right"
                    trailingText="%"
                    placeholder="0"
                    // Regex to validate any positive or negative number with a maximum of 4 decimal places
                    regexExp={'^[-]?[0-9]*.?[0-9]{0,4}$'}
                    defaultValue={defaultValue}
                    autoFocus={autoFocus}
                    onValueChange={onValueChange}
                    showLeftIcon={showIcon}
                />
            );
        default:
            return <></>;
    }
}

export default CWFieldInput;

type FieldInputProps = {
    autoFocus?: boolean;
    type?: 'text' | 'date';
    textAlign?: TextAlignment;
    placeholder?: string;
    trailingText?: string;
    characterLimit?: number;
    regexExp?: string;
    showLeftIcon?: boolean;
    showDropdown?: boolean;
    fieldType: FieldType;
    defaultValue: string | number | null;
    dropdownOptions?: { label: string; value: string }[];
    onValueChange: (v: string | number | null) => void;
};

function FieldInput({
    autoFocus = true,
    type = 'text',
    textAlign,
    placeholder,
    trailingText,
    characterLimit,
    regexExp,
    showLeftIcon = true,
    showDropdown = false,
    fieldType,
    defaultValue,
    dropdownOptions,
    onValueChange,
}: FieldInputProps): JSX.Element {
    const [value, setValue] = useState(defaultValue);
    const [dropdownValue, setDropdownValue] = useState<string | undefined>(undefined);

    /**
     * If regex expression is provided, test if input value is valid
     */
    function handleValidator(regexExp: string, val: string): boolean {
        const re = new RegExp(regexExp);
        return re.test(val);
    }

    function handleChange(value: string): void {
        setValue(value);

        const formattedValue = formatFieldValueToUpdate(fieldType, value, dropdownValue ? (dropdownValue as NatureEnum) : undefined);
        onValueChange(formattedValue);
    }

    useEffect(() => {
        if (fieldType === FieldType.ACCOUNTING && typeof defaultValue !== 'string') {
            if (defaultValue !== null && defaultValue < 0) {
                setDropdownValue('CREDIT');
            } else {
                setDropdownValue('DEBIT');
            }
        }

        const displayValue = formatFieldValueToDisplay(fieldType, defaultValue, true);
        setValue(displayValue);
    }, [defaultValue, fieldType]);

    return (
        <div className="w-full">
            {fieldType === FieldType.MONEY || fieldType === FieldType.ACCOUNTING ? (
                <CurrencyInputV2
                    autoFocus={autoFocus}
                    leftIcon={showLeftIcon ? <FieldIcon type={fieldType} /> : undefined}
                    placeholder={placeholder || '0.00'}
                    value={value === null ? '' : value.toString()}
                    onChange={handleChange}
                    textAlign={textAlign}
                >
                    {showDropdown && dropdownOptions && (
                        <InputV2.Dropdown
                            options={dropdownOptions}
                            name="nature"
                            value={dropdownValue}
                            onChange={(e): void => {
                                setDropdownValue(e.target.value);

                                if (value !== null) {
                                    const formattedValue = formatFieldValueToUpdate(fieldType, value, e.target.value as NatureEnum);
                                    onValueChange(formattedValue);
                                }
                            }}
                        />
                    )}
                </CurrencyInputV2>
            ) : (
                <InputV2
                    autoFocus={autoFocus}
                    characterLimit={characterLimit}
                    showCharacterLimitHint={false}
                    placeholder={placeholder || 'Enter a value'}
                    type={type}
                    trailingText={trailingText}
                    textAlign={textAlign}
                    leftIcon={showLeftIcon ? <FieldIcon type={fieldType} /> : undefined}
                    validator={regexExp ? (val): boolean => handleValidator(regexExp, val) : undefined}
                    onChange={handleChange}
                    value={value === null ? '' : value.toString()}
                />
            )}
        </div>
    );
}
